import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageSquareText, Check, ChevronRight, ArrowRight } from "lucide-react";
import { Seo, SITE_URL } from "../seo";
import { SERVICES, serviceJsonLd, breadcrumbJsonLd, faqJsonLd } from "../data/services";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { PHONE_TEL, PHONE_SMS, PHONE_DISPLAY } from "../site";

export default function ServicePage() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/" replace />;

  const others = SERVICES.filter((x) => x.slug !== slug);

  return (
    <main className="pt-28 md:pt-36 pb-24">
      <Seo
        title={s.title}
        description={s.meta}
        path={`/services/${s.slug}`}
        jsonLd={[serviceJsonLd(s, SITE_URL), breadcrumbJsonLd(s, SITE_URL), faqJsonLd(s.faqs)]}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <nav aria-label="Breadcrumb" data-testid="service-breadcrumb" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 mb-10">
          <Link to="/" className="hover:text-zinc-950 transition-colors">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <a href="/#services" className="hover:text-zinc-950 transition-colors">Services</a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-zinc-950">{s.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 20 }}
            className="lg:col-span-7"
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">
              Arrow Locksmith — Winnipeg, MB
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.02] text-zinc-950">
              {s.h1}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-zinc-600 max-w-2xl">{s.intro}</p>

            <ul className="mt-8 space-y-3">
              {s.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm md:text-base text-zinc-700">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10">
                    <Check className="h-3.5 w-3.5 text-brand" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-8 text-base leading-relaxed text-zinc-600 max-w-2xl">{s.local}</p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={PHONE_TEL}
                data-testid="service-call-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-alert px-7 py-4 text-base font-semibold text-white shadow-[0_0_28px_rgba(255,59,48,0.35)] hover:bg-red-600 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <Phone className="h-5 w-5" />
                Call {PHONE_DISPLAY}
              </a>
              <a
                href={PHONE_SMS}
                data-testid="service-text-button"
                className="inline-flex items-center gap-2.5 rounded-full border border-zinc-300 bg-white px-7 py-4 text-base font-semibold text-zinc-950 hover:border-zinc-950 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <MessageSquareText className="h-5 w-5" />
                Text us
              </a>
              <Link
                to="/contact"
                data-testid="service-quote-link"
                className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
              >
                Or request a quote online <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.15 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl overflow-hidden border border-zinc-200 shadow-[0_32px_80px_rgba(0,0,0,0.10)] aspect-[4/3]">
              <img src={s.img} alt={s.alt} className="h-full w-full object-cover" data-testid="service-image" />
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">Common questions</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-zinc-950">
              {s.name} — answered.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible data-testid="service-faq-accordion" className="w-full">
              {s.faqs.map((f, i) => (
                <AccordionItem key={i} value={`sf-${i}`} className="border-zinc-200">
                  <AccordionTrigger
                    data-testid={`service-faq-trigger-${i}`}
                    className="font-heading text-base md:text-lg font-medium tracking-tight text-zinc-950 hover:no-underline hover:text-brand py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base leading-relaxed text-zinc-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <div className="mt-24 border-t border-zinc-200 pt-14">
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-zinc-950 mb-8">
            More locksmith services in Winnipeg
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                data-testid={`service-related-${o.slug}`}
                className="group flex items-center justify-between rounded-2xl border border-zinc-200 bg-white px-5 py-4 hover:border-brand hover:-translate-y-0.5 transition-[border-color,transform] duration-200"
              >
                <span className="font-heading font-medium text-zinc-950 group-hover:text-brand transition-colors">{o.name}</span>
                <ArrowRight className="h-4 w-4 text-zinc-400 group-hover:text-brand transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
